import { useEffect, useRef } from 'react';
import { byKind, BranchEditProps, BranchRenderProps, ByKindBranches } from '../columns/createPlanColumn';
import type { Column } from 'components/DataGrid';
import type { GridRow, GridSummaryRow, RowOrGroup } from '../../types';
import type { Position } from 'modules/campaign/row';
import type { Calendar } from './types';
import { Header } from './Header';
export { useCalendar } from './useCalendar';
import { Edit } from './Edit';
import { View } from './View';
import { column } from './style.css';
import { usePlanMutations } from '../../state';
import { openPositionDialog } from './PositionDialog';

function stripPositionId({ date_from, date_to, quantity }: Position): Omit<Position, 'id'> {
  return { date_from, date_to, quantity };
}

/**
 * Keyboard path into positions — mouse interactions live entirely in the view
 * (drag on the canvas, Shift+click), so this editor's only job is opening the
 * same PositionDialog on mount and closing the cell when it settles.
 */
function EditPositions({ row, onClose, calendar }: BranchEditProps<GridRow> & { calendar: Calendar }): null {
  const { update, requestHandler } = usePlanMutations();
  const opened = useRef(false);
  useEffect(() => {
    if (opened.current) return;
    opened.current = true;
    void openPositionDialog(row, calendar.interval, calendar.resolution, update, requestHandler, null).finally(() =>
      onClose(false, true),
    );
  });
  return null;
}

function formatPosition({ date_from, date_to, quantity }: Position): string {
  return `${date_from}-${date_to} (${quantity})`;
}

export function createCalendarColumn(
  calendar: Calendar,
  branches?: Partial<ByKindBranches>,
): Column<RowOrGroup, GridSummaryRow> {
  const view = (props: BranchRenderProps<GridRow>) =>
    props.cellState.locked ? (
      <View {...props} calendar={calendar} />
    ) : (
      <Edit {...props} calendar={calendar} key={props.row.modified_at} />
    );

  const positionsBranch = {
    field: 'positions' as const,
    edit: (props: BranchEditProps<GridRow>) => <EditPositions {...props} calendar={calendar} />,
    displayCellContent: true,
    view,
    clipboard: {
      scalar: (row: GridRow) => row.positions.map(formatPosition).join('; '),
      // Drop `id` — it's row-local identity. The id-free payload replaces the
      // target's set, which the backend recreates as new positions on paste
      // (default paste: `{ id: row.id, positions: value }`).
      copy: (row: GridRow) => row.positions.map(stripPositionId),
    },
  };

  return byKind({
    key: 'calendar',
    name: <Header calendar={calendar} />,
    width: calendar.width,
    className: column,
    branches: {
      media: positionsBranch,
      service: positionsBranch,
      ...branches,
    },
  });
}
